import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { Trans, useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { useCreatePromoterMutation, useGetHubSpotStatusQuery, useLazyGetHubSpotAuthUrlQuery, useLazySearchHubSpotContactsQuery, HubSpotContact } from '../../store/promoterApi';
import { useGetVendorMeQuery } from '../../store/vendorApi';
import { CreatePromoterRequest, INDUSTRY_SECTORS, DEFAULT_INDUSTRY_SECTORS } from '@clareefai/types';
import { ChevronLeft, ChevronRight, Check, Mail, User, Building, Phone, Linkedin, Users, Tag, X, Globe, Package, Search, Briefcase } from 'lucide-react';
import { PhoneInput, toast, toastMessages, ImageUploadWithCrop } from '@clareefai/ui';
import { uploadFile } from '../../utils/upload';
import { useLogoSuggestion } from '../shared/useLogoSuggestion';
import LogoSuggestionCard from '../shared/LogoSuggestionCard';
import EditableEmailPreviewCard from '../shared/EditableEmailPreviewCard';
import { EmailTemplateType, useRenderEmailPreviewMutation } from '../../store/emailTemplateApi';

interface PromoterFormProps {
  onClose: () => void;
  initialValues?: Partial<CreatePromoterRequest>;
}

const LINKEDIN_PERSON_REGEX = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+/;

const companySizes = ['1-10', '11-50', '51-200', '201-500', '501-1000', '+1000'] as const;
type CompanySize = typeof companySizes[number];

const createPromoterSchema = v.object({
  first_name: v.pipe(
    v.string(),
    v.minLength(2, 'First name must be at least 2 characters'),
    v.maxLength(50, 'First name must not exceed 50 characters')
  ),
  last_name: v.pipe(
    v.string(),
    v.minLength(2, 'Last name must be at least 2 characters'),
    v.maxLength(50, 'Last name must not exceed 50 characters')
  ),
  job_title: v.optional(
    v.union([
      v.pipe(v.string(), v.maxLength(100, 'Job title must not exceed 100 characters')),
      v.literal('')
    ])
  ),
  email: v.pipe(
    v.string(),
    v.email('Please enter a valid email address')
  ),
  phone: v.optional(
    v.union([
      v.string(),
      v.literal('')
    ])
  ),
  company: v.optional(
    v.union([
      v.string(),
      v.literal('')
    ])
  ),
  linkedin: v.optional(
    v.union([
      v.pipe(
        v.string(),
        v.regex(LINKEDIN_PERSON_REGEX, 'Must be a valid LinkedIn profile URL (e.g. https://linkedin.com/in/username)')
      ),
      v.literal('')
    ])
  ),
  company_url: v.pipe(
    v.string('Company URL is required'),
    v.minLength(1, 'Company URL is required'),
    v.url('Please enter a valid URL')
  ),
  avatar: v.optional(v.string()),
  company_logo: v.optional(v.string()),
  company_size: v.picklist(companySizes, 'Please select a company size'),
  industry_sectors: v.pipe(
    v.array(v.string()),
    v.minLength(1, 'Please select at least one sector')
  ),
  product_names: v.optional(v.array(v.string())),
});

const PromoterForm: React.FC<PromoterFormProps> = ({ onClose, initialValues }) => {
  const { t } = useTranslation();
  const { data: vendor } = useGetVendorMeQuery();
  const [createPromoter, { isLoading, error }] = useCreatePromoterMutation();
  const [renderEmailPreview, { data: renderedPreview, isLoading: isRendering }] = useRenderEmailPreviewMutation();
  const [currentStep, setCurrentStep] = useState(1);
  const [submittedData, setSubmittedData] = useState<CreatePromoterRequest | null>(null);
  const [showHubSpotModal, setShowHubSpotModal] = useState(false);
  const [hubSpotQuery, setHubSpotQuery] = useState('');
  const [emailOverride, setEmailOverride] = useState<{ subject: string; body: string } | null>(null);

  const { data: hubSpotStatus, refetch: refetchHubSpotStatus } = useGetHubSpotStatusQuery();
  const [getHubSpotAuthUrl] = useLazyGetHubSpotAuthUrlQuery();
  const [searchHubSpotContacts, { data: hubSpotResults, isFetching: isSearching }] = useLazySearchHubSpotContactsQuery();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isValid },
    trigger
  } = useForm<CreatePromoterRequest>({
    resolver: valibotResolver(createPromoterSchema),
    mode: 'onChange',
    defaultValues: {
      first_name: initialValues?.first_name ?? '',
      last_name: initialValues?.last_name ?? '',
      job_title: initialValues?.job_title ?? '',
      email: initialValues?.email ?? '',
      phone: initialValues?.phone ?? '',
      company: initialValues?.company ?? '',
      linkedin: initialValues?.linkedin ?? '',
      company_url: initialValues?.company_url ?? '',
      avatar: initialValues?.avatar ?? '',
      company_logo: (initialValues as any)?.company_logo ?? '',
      company_size: initialValues?.company_size ?? undefined,
      industry_sectors: initialValues?.industry_sectors ?? [],
      product_names: initialValues?.product_names ?? []
    }
  });

  const watchedValues = watch();
  const selectedSectors = watchedValues.industry_sectors || [];

  const logoSuggestion = useLogoSuggestion(
    watchedValues.company_logo || '',
    (logo) => setValue('company_logo', logo, { shouldDirty: true }),
  );
  const industrySectors = vendor?.preferredIndustries && vendor.preferredIndustries.length > 0
    ? vendor.preferredIndustries
    : DEFAULT_INDUSTRY_SECTORS;

  const vendorProducts = vendor?.pageInfo?.products ?? [];
  const hasProducts = vendorProducts.length > 0;
  const selectedProducts = watchedValues.product_names || [];

  const handleHubSpotButtonClick = async () => {
    if (hubSpotStatus?.connected) {
      setShowHubSpotModal(true);
    } else {
      const statusResult = await refetchHubSpotStatus();
      if (statusResult.data?.connected) {
        setShowHubSpotModal(true);
        return;
      }
      const urlResult = await getHubSpotAuthUrl();
      if (urlResult.data?.auth_url) {
        window.open(urlResult.data.auth_url, '_blank');
      }
    }
  };

  const handleHubSpotSearch = () => {
    if (hubSpotQuery.trim()) {
      searchHubSpotContacts({ q: hubSpotQuery.trim() });
    }
  };

  const handleHubSpotSelect = (contact: HubSpotContact) => {
    const p = contact.properties;
    if (p.firstname) setValue('first_name', p.firstname, { shouldValidate: true });
    if (p.lastname) setValue('last_name', p.lastname, { shouldValidate: true });
    if (p.email) setValue('email', p.email, { shouldValidate: true });
    if (p.phone) setValue('phone', p.phone ?? '', { shouldValidate: true });
    if (p.company) setValue('company', p.company ?? '', { shouldValidate: true });
    setShowHubSpotModal(false);
    setHubSpotQuery('');
  };

  const handleProductToggle = (productName: string) => {
    const current = selectedProducts;
    if (current.includes(productName)) {
      const updated = current.filter(p => p !== productName);
      setValue('product_names', updated.length > 0 ? updated : [], { shouldValidate: true });
    } else {
      setValue('product_names', [...current, productName], { shouldValidate: true });
    }
  };

  const handleSectorToggle = (sector: string) => {
    const currentSectors = selectedSectors;
    if (currentSectors.includes(sector)) {
      // Remove sector if already selected
      const newSectors = currentSectors.filter(s => s !== sector);
      setValue('industry_sectors', newSectors.length > 0 ? newSectors : undefined, { shouldValidate: true });
    } else {
      // Add sector only if under limit
      if (currentSectors.length < 6) {
        const newSectors = [...currentSectors, sector];
        setValue('industry_sectors', newSectors, { shouldValidate: true });
      }
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const isStep1Valid = await trigger(['first_name', 'last_name', 'job_title', 'email', 'phone', 'company', 'linkedin', 'company_url', 'company_size', 'industry_sectors', 'product_names']);
      if (isStep1Valid) {
        renderEmailPreview({
          templateType: EmailTemplateType.PROMOTER_MAGIC_LINK,
          firstName: watchedValues.first_name || 'there',
          email: watchedValues.email || '',
          productNames: watchedValues.product_names ?? [],
        });
        setCurrentStep(2);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data: CreatePromoterRequest) => {
    try {
      const payload: CreatePromoterRequest = {
        first_name: data.first_name,
        last_name: data.last_name,
        job_title: data.job_title && data.job_title.trim() ? data.job_title.trim() : undefined,
        email: data.email,
        phone: data.phone && data.phone.trim() ? data.phone.trim() : undefined,
        company: data.company && data.company.trim() ? data.company.trim() : undefined,
        linkedin: data.linkedin && data.linkedin.trim() ? data.linkedin.trim() : undefined,
        company_url: data.company_url && data.company_url.trim() ? data.company_url.trim() : undefined,
        // Use the uploaded picture; if none, fall back to the company logo when
        // it exists; otherwise leave empty.
        avatar:
          (data.avatar && data.avatar.trim()) ||
          (data.company_logo && data.company_logo.trim()) ||
          undefined,
        company_logo: data.company_logo && data.company_logo.trim() ? data.company_logo.trim() : undefined,
        company_size: data.company_size || undefined,
        industry_sectors: data.industry_sectors && data.industry_sectors.length > 0 ? data.industry_sectors : undefined,
        product_names: data.product_names && data.product_names.length > 0 ? data.product_names : undefined,
        emailSubject: emailOverride?.subject,
        emailBody: emailOverride?.body,
      };
      
      setSubmittedData(payload);
      await createPromoter(payload).unwrap();
      toast.success(toastMessages.promoter.created);
      setCurrentStep(3);
    } catch (err: any) {
      console.error('Failed to create promoter:', err);
      toast.error(err?.data?.message || toastMessages.generic.error);
    }
  };

  const emailSampleData = {
    firstName: watchedValues.first_name || 'there',
    companyName: vendor?.companyName || '[Company Name]',
    vendorName: vendor ? [vendor.firstName, vendor.lastName].filter(Boolean).join(' ') : '[Vendor Name]',
    invitationReason: 'Share your success story',
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 font-semibold ${
            step <= currentStep 
              ? 'bg-purple-600 border-purple-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-6 h-6" /> : step}
          </div>
          {step < 3 && (
            <div className={`w-20 h-0.5 ${
              step < currentStep ? 'bg-purple-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('promoters.form.step1Title')}</h3>
          <p className="text-gray-600">{t('promoters.form.step1Subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={handleHubSpotButtonClick}
          className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap ${
            hubSpotStatus?.connected
              ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Search className="w-4 h-4" />
          {hubSpotStatus?.connected ? t('promoters.form.importFromHubSpot') : t('promoters.form.connectToHubSpot')}
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-32 flex-shrink-0">
          <Controller
            name="avatar"
            control={control}
            render={({ field }) => (
              <ImageUploadWithCrop
                value={field.value || ''}
                onChange={field.onChange}
                onUpload={(blob) => uploadFile(blob, 'avatars')}
                shape="round"
                aspect={1}
              />
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('promoters.form.picture')}
          </label>
          <p className="text-xs text-gray-500 mt-1 max-w-xs">{t('promoters.form.pictureHint')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('promoters.form.firstNameRequired')}
          </label>
          <div className="space-y-0">
            <input
              type="text"
              {...register('first_name')}
              className={`form-input ${errors.first_name ? 'border-red-500' : ''}`}
              placeholder={t('promoters.form.firstNamePlaceholder')}
            />
            <div className="h-5 mt-1">
              {errors.first_name && (
                <p className="text-sm text-red-600">{errors.first_name.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('promoters.form.lastNameRequired')}
          </label>
          <div className="space-y-0">
            <input
              type="text"
              {...register('last_name')}
              className={`form-input ${errors.last_name ? 'border-red-500' : ''}`}
              placeholder={t('promoters.form.lastNamePlaceholder')}
            />
            <div className="h-5 mt-1">
              {errors.last_name && (
                <p className="text-sm text-red-600">{errors.last_name.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-1" />
            {t('promoters.form.emailRequired')}
          </label>
          <div className="space-y-0">
            <input
              type="email"
              {...register('email')}
              className={`form-input ${errors.email ? 'border-red-500' : ''}`}
              placeholder={t('promoters.form.emailPlaceholder')}
            />
            <div className="h-5 mt-1">
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-1" />
            {t('common.phone')}
          </label>
          <div className="space-y-0">
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('promoters.form.phonePlaceholder')}
                  error={!!errors.phone}
                />
              )}
            />
            <div className="h-5 mt-1">
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Briefcase className="w-4 h-4 inline mr-1" />
            {t('common.jobTitle')}
          </label>
          <div className="space-y-0">
            <input
              type="text"
              {...register('job_title')}
              className={`form-input ${errors.job_title ? 'border-red-500' : ''}`}
              placeholder={t('common.jobTitle')}
            />
            <div className="h-5 mt-1">
              {errors.job_title && (
                <p className="text-sm text-red-600">{errors.job_title.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="w-4 h-4 inline mr-1" />
            {t('common.company')}
          </label>
          <input
            type="text"
            {...register('company')}
            className="form-input"
            placeholder={t('promoters.form.companyPlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Linkedin className="w-4 h-4 inline mr-1" />
            {t('common.linkedin')}
          </label>
          <div className="space-y-0">
            <input
              type="url"
              {...register('linkedin')}
              className={`form-input ${errors.linkedin ? 'border-red-500' : ''}`}
              placeholder={t('promoters.form.linkedinPlaceholder')}
            />
            <div className="h-5 mt-1">
              {errors.linkedin && (
                <p className="text-sm text-red-600">{errors.linkedin.message}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Globe className="w-4 h-4 inline mr-1" />
            {t('promoters.form.companyUrlRequired')}
          </label>
          <div className="space-y-0">
            <Controller
              name="company_url"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="url"
                  className={`form-input ${errors.company_url ? 'border-red-500' : ''}`}
                  placeholder={t('promoters.form.companyUrlPlaceholder')}
                  onBlur={(e) => {
                    field.onBlur();
                    logoSuggestion.lookup(e.target.value);
                  }}
                />
              )}
            />
            <LogoSuggestionCard
              suggestion={logoSuggestion.suggestion}
              checking={logoSuggestion.checking}
              onApply={logoSuggestion.apply}
              onDismiss={logoSuggestion.dismiss}
            />
            <div className="h-5 mt-1">
              {errors.company_url && (
                <p className="text-sm text-red-600">{errors.company_url.message}</p>
              )}
            </div>
          </div>
        </div>
      </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            {t('promoters.form.companySizeRequired')}
          </label>
          <div className="space-y-0">
            <select
              {...register('company_size')}
              className={`form-input ${errors.company_size ? 'border-red-500' : ''}`}
            >
              <option value="">{t('promoters.form.selectSize')}</option>
              {companySizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <div className="h-5 mt-1">
              {errors.company_size && (
                <p className="text-sm text-red-600">{errors.company_size.message}</p>
              )}
            </div>
          </div>
        </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Tag className="w-4 h-4 inline mr-1" />
          {t('promoters.form.industrySectorsRequired')} {selectedSectors.length > 0 && t('promoters.form.industrySectorsCount', { count: selectedSectors.length })}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {industrySectors.map(sector => {
            const isSelected = selectedSectors.includes(sector);
            const isDisabled = !isSelected && selectedSectors.length >= 6;
            return (
              <button
                key={sector}
                type="button"
                onClick={() => handleSectorToggle(sector)}
                disabled={isDisabled}
                className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? 'bg-purple-100 border-purple-400 text-purple-800 shadow-md transform scale-105'
                    : isDisabled
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{sector}</span>
                  {isSelected && (
                    <Check className="w-4 h-4 text-purple-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="h-5 mt-1">
          {errors.industry_sectors && (
            <p className="text-sm text-red-600">{errors.industry_sectors.message}</p>
          )}
          {selectedSectors.length >= 6 && (
            <p className="text-sm text-gray-500">{t('promoters.form.maxSectors')}</p>
          )}
        </div>
        {selectedSectors.length > 0 && (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-700 font-medium mb-2">
              {t('promoters.form.selectedSectors', { count: selectedSectors.length })}
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedSectors.map(sector => (
                <span
                  key={sector}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                >
                  {sector}
                  <button
                    type="button"
                    onClick={() => handleSectorToggle(sector)}
                    className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {hasProducts && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Package className="w-4 h-4 inline mr-1" />
            {t('promoters.form.products')} {selectedProducts.length > 0 && t('promoters.form.productsCount', { count: selectedProducts.length, total: vendorProducts.length })}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {vendorProducts.map(product => {
              const isSelected = selectedProducts.includes(product.name);
              return (
                <button
                  key={product.name}
                  type="button"
                  onClick={() => handleProductToggle(product.name)}
                  className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'bg-purple-100 border-purple-400 text-purple-800 shadow-md transform scale-105'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{product.name}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {selectedProducts.length > 0 && (
            <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700 font-medium mb-2">
                {t('promoters.form.selectedProducts', { count: selectedProducts.length })}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedProducts.map(name => (
                  <span
                    key={name}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => handleProductToggle(name)}
                      className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => {
    const promoterDisplayName = `${watchedValues.first_name || ''} ${watchedValues.last_name || ''}`.trim() || t('promoters.form.defaultPromoter');
    return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('promoters.form.step2Title')}</h3>
        <p className="text-gray-600">{t('promoters.form.step2Subtitle')}</p>
      </div>

      <div className="flex items-center space-x-3 text-sm text-gray-600 mb-2 px-1">
        <Mail className="w-4 h-4 text-blue-500" />
        <span className="font-medium">{t('promoters.form.to')}</span>
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
          {watchedValues.email || t('promoters.form.defaultEmail')}
        </span>
      </div>

      <EditableEmailPreviewCard
        templateType={EmailTemplateType.PROMOTER_MAGIC_LINK}
        renderedHtml={renderedPreview?.body}
        renderedSubject={renderedPreview?.subject}
        isRendering={isRendering}
        sampleData={emailSampleData}
        override={emailOverride}
        onOverrideChange={setEmailOverride}
      />

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900 mb-1">{t('promoters.form.readyTitle')}</h4>
            <p className="text-sm text-green-700">
              <Trans i18nKey="promoters.form.readyMessage" values={{ name: promoterDisplayName }} components={{ 1: <strong /> }} />
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">{t('promoters.form.autoPersonalizationTitle')}</h4>
            <p className="text-sm text-blue-700">
              {t('promoters.form.autoPersonalizationDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderStep3 = () => {
    const data = submittedData || watchedValues;
    return (
      <div className="text-center space-y-8">
        <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
          <Check className="w-12 h-12 text-white" />
        </div>

        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('promoters.form.step3Great')} 🎉</h3>
          <p className="text-xl text-gray-600 mb-2">{t('promoters.form.step3Success')}</p>
          <p className="text-gray-500">{t('promoters.form.step3EmailSent')}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center justify-center">
            <User className="w-5 h-5 mr-2 text-purple-600" />
            {t('promoters.form.promoterSummary')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.nameLabel')}</span>
                <span className="text-gray-900">{`${data.first_name || ''} ${data.last_name || ''}`.trim()}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.emailLabel')}</span>
                <span className="text-gray-900">{data.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.phoneLabel')}</span>
                <span className="text-gray-900">{data.phone || t('promoters.form.notProvided')}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.companyLabel')}</span>
                <span className="text-gray-900">{data.company || t('promoters.form.notProvided')}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.sizeLabel')}</span>
                <span className="text-gray-900">{data.company_size || t('promoters.form.notSpecified')}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('promoters.form.statusLabel')}</span>
                <span className="status-badge status-invited">{t('promoters.form.statusInvited')}</span>
              </div>
            </div>
          </div>

          {data.industry_sectors && data.industry_sectors.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-200">
              <div className="text-left">
                <span className="font-medium text-gray-600 text-sm">{t('promoters.form.industrySectorsLabel')}</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {data.industry_sectors.map(sector => (
                    <span
                      key={sector}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {data.product_names && data.product_names.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-200">
              <div className="text-left">
                <span className="font-medium text-gray-600 text-sm">{t('promoters.form.productsLabel')}</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {data.product_names.map(name => (
                    <span
                      key={name}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 text-blue-700">
            <Mail className="w-5 h-5" />
            <span className="font-medium">{t('promoters.form.emailSentTo', { email: data.email })}</span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            <Trans i18nKey="promoters.form.appearsIn" components={{ 1: <strong /> }} />
          </p>
        </div>
      </div>
    );
  };

  const isStep1Valid = !errors.first_name && !errors.last_name && !errors.email && 
    watchedValues.first_name && watchedValues.last_name && watchedValues.email;

  return (
    <div className="space-y-8">
      {renderStepIndicator()}

      <div className="min-h-96">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200">
        {currentStep !== 3 && (
          <button
            type="button"
            onClick={currentStep === 1 ? onClose : handlePrevious}
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {currentStep === 1 ? (
              t('promoters.form.buttons.cancel')
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('promoters.form.buttons.previous')}
              </>
            )}
          </button>
        )}
        {currentStep === 3 && <div />}

        {currentStep === 1 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!isStep1Valid}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {t('promoters.form.buttons.next')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}

        {currentStep === 2 && (
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {isLoading ? t('promoters.form.buttons.creating') : t('promoters.form.buttons.sendEmail')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}

        {currentStep === 3 && (
          <button
            type="button"
            onClick={onClose}
            className="btn-primary px-6 py-3"
          >
            {t('promoters.form.buttons.close')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
          {error && 'data' in error && typeof error.data === 'object' && error.data !== null && 'message' in error.data
            ? (error.data as { message: string }).message
            : t('promoters.form.errors.createFailed')}
        </div>
      )}

      {showHubSpotModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">{t('promoters.form.hubspotModal.title')}</h3>
              <button
                type="button"
                onClick={() => { setShowHubSpotModal(false); setHubSpotQuery(''); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 border-b">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={hubSpotQuery}
                  onChange={e => setHubSpotQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleHubSpotSearch()}
                  placeholder={t('promoters.form.hubspotModal.searchPlaceholder')}
                  className="form-input flex-1"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleHubSpotSearch}
                  disabled={isSearching || !hubSpotQuery.trim()}
                  className="btn-primary px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? '...' : t('promoters.form.hubspotModal.search')}
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {hubSpotResults?.contacts.length === 0 && (
                <p className="text-center text-gray-500 py-8">{t('promoters.form.hubspotModal.noResults')}</p>
              )}
              {hubSpotResults?.contacts.map(contact => {
                const p = contact.properties;
                const name = [p.firstname, p.lastname].filter(Boolean).join(' ') || t('promoters.form.hubspotModal.unknown');
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleHubSpotSelect(contact)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{name}</div>
                    {p.email && <div className="text-sm text-gray-500">{p.email}</div>}
                    {p.company && <div className="text-xs text-gray-400">{p.company}</div>}
                  </button>
                );
              })}
              {!hubSpotResults && !isSearching && (
                <p className="text-center text-gray-400 py-8 text-sm">{t('promoters.form.hubspotModal.hint')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoterForm;